import { useState, useEffect, useMemo } from 'react';
import { BsBellFill } from "react-icons/bs";
import { io } from 'socket.io-client';
import {
    Box,
    Typography,
    Badge,
    List,
    ListItemText,
    Divider,
    Menu,
    IconButton,
    Tooltip,
    Avatar,
    ListItemAvatar,
    MenuItem,
    Link,
    Snackbar,
    Alert,
} from '@mui/material';
import '../styles/sidebar.css';
import axios from 'axios';
import { allowedRoutesByRole } from '../utils/routes';

const Notification = ({ currentUser, collapsed }) => {
    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [socket, setSocket] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [newNotification, setNewNotification] = useState(null);
    const open = Boolean(anchorEl);

    const unviewedCount = notifications.filter(n => !n.seen).length;

    /* ROUTE VARIABLES */
    const getAllowedRoutes = (role) => allowedRoutesByRole[role] || [];
    const allowedRoutes = useMemo(() => getAllowedRoutes(currentUser?.role) || [], [currentUser?.role]);

    // Helper function to check if a URL path is allowed
    const isUrlAllowed = (url) => {
        if (!url) return false;

        try {
            // Extract the pathname from the URL (handles both full URLs and paths)
            const urlObj = new URL(url, window.location.origin);
            const path = urlObj.pathname;

            // Check if the path exists in allowedRoutes
            return allowedRoutes.some(route => {
                // Handle routes with parameters (e.g., "/reports/report-documents/:trancheId/:fileType")
                const routeRegex = new RegExp('^' + route.replace(/:[^/]+/g, '([^/]+)') + '$');
                return routeRegex.test(path);
            });
        } catch (e) {
            // If URL parsing fails, treat it as a path
            const path = url.startsWith('/') ? url : `/${url}`;
            return allowedRoutes.some(route => {
                const routeRegex = new RegExp('^' + route.replace(/:[^/]+/g, '([^/]+)') + '$');
                return routeRegex.test(path);
            });
        }
    };

    /* SOCKET.IO SETUP */
    useEffect(() => {
        if (!currentUser?.id) return;

        const controller = new AbortController();
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const socketUrl = `${protocol}//${window.location.hostname}`;

        const newSocket = io(socketUrl, {
            query: { user_id: currentUser.id },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        setSocket(newSocket);

        newSocket.on('connect', () => {
            // Join the user-specific room
            newSocket.emit('join_room', { room: `user_${currentUser.id}` });
        });

        newSocket.on('notification', (data) => {
            const newNotif = {
                id: data.id,
                description: data.description,
                title: data.title,
                type: data.type || null,
                from_username: data.from_username,
                created_on: new Date(data.created_on),
                seen: data.seen || false,
                url: data.url
            };
            setNotifications(prev => [newNotif, ...prev]);
            setNewNotification(newNotif);
            setSnackbarOpen(true);
        });

        newSocket.on('disconnect', () => {
        });

        newSocket.on('connect_error', (error) => {
        });

        const fetchNotifications = async () => {
            try {
                const response = await axios({
                    method: 'get',
                    url: `/api/GetNotifications/${currentUser.id}`,
                    signal: controller.signal
                });

                const { status, data } = response;

                if (status === 200) {
                    setNotifications(data.map(n => ({
                        id: n.id,
                        description: n.description,
                        title: n.title,
                        type: n.type || null,
                        from_username: n.from_username,
                        created_on: new Date(n.created_on),
                        seen: n.seen,
                        url: n.url
                    })));
                } else if (status === 204) {
                    setNotifications([]);
                } else {
                    throw new Error(`Unexpected status code: ${status}`);
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }

                setNotifications([]);
            }
        };

        fetchNotifications();

        return () => {
            controller.abort();
            if (newSocket) {
                newSocket.disconnect();
            }
        };
    }, [currentUser]);

    /* MARK NOTIFICATIONS AS SEEN */
    useEffect(() => {
        if (open && notifications.some(n => !n.seen)) {
            const unreadIds = notifications
                .filter(n => !n.seen)
                .map(n => n.id);

            if (socket && socket.connected) {
                socket.emit('mark_notifications_read', {
                    ids: unreadIds,
                    user_id: currentUser.id
                });
                setNotifications(prev => prev.map(n =>
                    unreadIds.includes(n.id) ? { ...n, seen: true } : n
                ));
            }
        }
    }, [open, notifications, socket, currentUser]);

    const linkify = (text) => {
        if (!text) return text;

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part, i) => {
            if (part.match(urlRegex)) {
                return (
                    <Link
                        key={i}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                        {part}
                    </Link>
                );
            }
            return part;
        });
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
        setNewNotification(null);
    };

    const formatTimeAgo = (date) => {
        const seconds = Math.floor((new Date() - date) / 1000);
        const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
            second: 1
        };

        for (const [unit, secondsInUnit] of Object.entries(intervals)) {
            const interval = Math.floor(seconds / secondsInUnit);
            if (interval >= 1) {
                return `${interval} ${unit}${interval === 1 ? '' : 's'} ago`;
            }
        }
        return 'just now';
    };

    return (
        <Box className="notification-container">
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{
                        ml: 2,
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    }}
                    aria-controls={open ? 'notifications-menu' : undefined}
                >
                    <Badge badgeContent={unviewedCount} color="error">
                        <BsBellFill size={collapsed ? 20 : 18} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            maxHeight: 450,
                            width: '50%',
                            left: '115px !important',
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontSize: '14px',
                        padding: '5px 16px',
                        textAlign: 'center',
                        backgroundColor: '#0C969C',
                        color: '#fff',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #ddd',
                    }}
                >
                    Notifications
                    {unviewedCount > 0 && (
                        <Typography component="span" sx={{
                            fontSize: '12px',
                            ml: 1,
                            backgroundColor: '#fff',
                            color: '#0C969C',
                            borderRadius: '10px',
                            padding: '2px 8px',
                        }}>
                            {unviewedCount} new
                        </Typography>
                    )}
                </Typography>

                {notifications.length === 0 ? (
                    <MenuItem sx={{ py: 2, textAlign: 'center' }}>
                        <ListItemText primary="No notifications" sx={{ fontSize: '12px' }} />
                    </MenuItem>
                ) : (
                    <List disablePadding sx={{ width: '100%', height: '400px', overflow: 'auto', fontSize: '12px' }}>
                        {notifications.map((notification) => {
                            const isAllowed = isUrlAllowed(notification.url);

                            return (
                                <Box key={notification.id}>
                                    <MenuItem
                                        sx={{
                                            padding: '12px 16px',
                                            backgroundColor: notification.seen ? 'inherit' : 'rgba(25, 118, 210, 0.08)',
                                            cursor: isAllowed ? 'pointer' : 'default',
                                        }}
                                        component={isAllowed ? Link : 'div'}
                                        href={isAllowed ? notification.url : undefined}
                                        onClick={(e) => {
                                            if (!isAllowed) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#0C969C',
                                                    color: '#ffffff',
                                                    fontSize: '12px !important',
                                                }}
                                            >
                                                {notification.from_username?.charAt(0).toUpperCase()}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" sx={{ fontSize: '12px', fontWeight: notification.seen ? 'normal' : 'bold' }}>
                                                    {notification.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '12px',
                                                            display: 'block',
                                                            color: notification.seen ? 'text.secondary' : 'text.primary',
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                    >
                                                        {linkify(notification.description)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: '11px !important',
                                                            display: 'block',
                                                            color: 'text.disabled',
                                                            mt: 0.5,
                                                        }}
                                                    >
                                                        {formatTimeAgo(notification.created_on)} • From {notification.from_username}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </MenuItem>
                                    <Divider />
                                </Box>
                            );
                        })}
                    </List>
                )}
            </Menu>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="info"
                    sx={{ width: '100%' }}
                    icon={<BsBellFill />}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {newNotification?.title}
                    </Typography>
                    <Typography variant="body2">
                        {newNotification?.description}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        From {newNotification?.from_username} • {newNotification && formatTimeAgo(newNotification.created_on)}
                    </Typography>
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Notification;